/**
 * Paxton + ZKTeco + 2N access-control catalog — readers + controllers.
 *
 * All specs verified against paxton-access.com / zkteco.com / 2n.com
 * product pages on 2026-05-15. URL cited per entry.
 *
 * Schema normalisation applied:
 *   - technology enum is RFID-only ('em-marin-125khz' | 'mifare' |
 *     'dallas-ibutton' | 'wiegand-only'); biometric methods (face,
 *     fingerprint, palm) and mobile (Bluetooth/NFC) are described in
 *     `tagline` since they have no enum slot.
 *   - 'paxton-125khz' / 'hid-prox-125khz' / 'paxton' → 'em-marin-125khz'
 *     (all are 125 kHz proximity classes)
 *   - 'mifare-13-56mhz' / 'mifare-desfire*' / 'hid-iclass' → 'mifare'
 *   - protocol enum: 'wiegand-26' / 'wiegand-34' / 'wiegand-42' /
 *     'wiegand-56' / 'osdp' / 'satel-bus'. Source 'rs485' and
 *     'rs485-osdp' → 'osdp'. Source 'wiegand' (unspecified bits) →
 *     'wiegand-26'. Source 'tcp-ip', 'wifi', 'usb' dropped.
 *   - 'wireless-net2air' hostBus (Paxton Net2 Nano) → 'standalone'.
 *
 * Net2Air Bridge has no `doors` count — listed with `doors: 1` as
 * placeholder since the schema requires the field; tagline notes that
 * it's a wireless bridge, not a door controller per se.
 */

import type { EquipmentCatalogEntry } from './equipmentCatalogEntry';

export const PAXTON_ZKTECO_ACCESS_CATALOG: EquipmentCatalogEntry[] = [
  // ─────────────────────────────────────────────────────────────
  // Paxton — readers (Net2 P/KP series + Paxton10)
  // ─────────────────────────────────────────────────────────────

  {
    id: 'paxton-p50',
    manufacturer: 'Paxton',
    model: 'Net2 Proximity Reader P50',
    category: 'access-reader',
    tagline: 'Net2 / Switch2 proximity reader, 125 kHz HID-compatible with activation card',
    verified: true,
    datasheetUrl: 'https://www.paxton-access.com/products/proximity-reader-p50/',
    specs: {
      technology: ['em-marin-125khz'],
      outdoor: true,
      keypad: false,
    },
  },

  {
    id: 'paxton-kp50',
    manufacturer: 'Paxton',
    model: 'Net2 Proximity Keypad KP50',
    category: 'access-reader',
    tagline: 'Combined keypad + proximity reader for card-plus-PIN access on Net2 / Switch2',
    verified: true,
    datasheetUrl: 'https://www.paxton-access.com/products/proximity-keypad-kp50/',
    specs: {
      technology: ['em-marin-125khz'],
      outdoor: true,
      keypad: true,
    },
  },

  {
    id: 'paxton-p75',
    manufacturer: 'Paxton',
    model: 'Net2 Proximity Reader P75',
    category: 'access-reader',
    tagline: 'Larger-format Paxton proximity reader, 100 mm read range',
    verified: true,
    datasheetUrl: 'https://www.paxton-access.com/products/proximity-reader-p75/',
    specs: {
      technology: ['em-marin-125khz'],
      outdoor: true,
      keypad: false,
    },
  },

  {
    id: 'paxton-kp75',
    manufacturer: 'Paxton',
    model: 'Net2 Proximity Keypad KP75',
    category: 'access-reader',
    tagline: 'Card-plus-PIN keypad reader for Net2 / Switch2, 100 mm read range',
    verified: true,
    datasheetUrl: 'https://www.paxton-access.com/us/products/proximity-keypad-kp75/',
    specs: {
      technology: ['em-marin-125khz'],
      outdoor: true,
      keypad: true,
    },
  },

  {
    id: 'paxton-compact-p50',
    manufacturer: 'Paxton',
    model: 'Compact Proximity Reader P50',
    category: 'access-reader',
    tagline: 'Standalone Compact reader with onboard electronics, up to 10,000 users',
    verified: true,
    datasheetUrl: 'https://www.paxton-access.com/products/compact-proximity-reader-p50/',
    specs: {
      technology: ['em-marin-125khz'],
      keypad: false,
    },
  },

  {
    id: 'paxton10-slimline-reader',
    manufacturer: 'Paxton',
    model: 'Paxton10 Slimline Reader',
    category: 'access-reader',
    tagline: 'Multi-format mullion reader for Paxton10, IP67/IK10 externally rated',
    verified: true,
    datasheetUrl: 'https://www.paxton-access.com/products/paxton10-slimline-reader/',
    specs: {
      technology: ['em-marin-125khz', 'mifare'],
      outdoor: true,
      keypad: false,
      ipRating: 'IP67',
    },
  },

  // ─────────────────────────────────────────────────────────────
  // Paxton — controllers
  // ─────────────────────────────────────────────────────────────

  {
    id: 'paxton-net2-plus-1-door',
    manufacturer: 'Paxton',
    model: 'Net2 Plus 1 door controller',
    category: 'door-controller',
    tagline: 'Net2 networkable 1-door controller, TCP/IP with optional PoE+ and RS485 chaining',
    verified: true,
    datasheetUrl: 'https://www.paxton-access.com/products/net2-plus-1-door-controller/',
    specs: {
      doors: 1,
      hostBus: 'tcp-ip',
    },
  },

  {
    id: 'paxton-net2-nano',
    manufacturer: 'Paxton',
    model: 'Net2 Nano 1 door controller',
    category: 'door-controller',
    tagline: 'Wireless 1-door Net2 controller, communicates via Net2Air bridge',
    verified: true,
    datasheetUrl: 'https://www.paxton-access.com/products/net2-nano-1-door-controller-12v-2a-psu-with-plastic-cabinet/',
    specs: {
      doors: 1,
      hostBus: 'standalone',
    },
  },

  {
    id: 'paxton-net2-entry-control-unit',
    manufacturer: 'Paxton',
    model: 'Net2 Entry Control Unit',
    category: 'door-controller',
    tagline: 'Single-door video entry controller, 4-port PoE switch for monitors and panels',
    verified: true,
    datasheetUrl: 'https://www.paxton-access.com/products/entry-control-unit/',
    specs: {
      doors: 1,
      hostBus: 'tcp-ip',
    },
  },

  {
    id: 'paxton10-door-controller',
    manufacturer: 'Paxton',
    model: 'Paxton10 Door Controller',
    category: 'door-controller',
    tagline: 'Paxton10 single-door networked controller, scales to 1,000 doors per system',
    verified: true,
    datasheetUrl: 'https://www.paxton-access.com/us/products/paxton10-door-controller-with-plastic-housing/',
    specs: {
      doors: 1,
      hostBus: 'tcp-ip',
    },
  },

  {
    id: 'paxton-net2air-bridge',
    manufacturer: 'Paxton',
    model: 'Net2Air Bridge',
    category: 'door-controller',
    tagline: 'Wireless bridge linking Net2 PC to Net2 Nano and PaxLock Pro units (not a door controller per se)',
    verified: true,
    datasheetUrl: 'https://www.paxton-access.com/products/net2air-bridge/',
    specs: {
      doors: 1,
      hostBus: 'tcp-ip',
    },
  },

  // ─────────────────────────────────────────────────────────────
  // ZKTeco — biometric/RFID readers
  // ─────────────────────────────────────────────────────────────

  {
    id: 'zkteco-probio',
    manufacturer: 'ZKTeco',
    model: 'ProBio',
    category: 'access-reader',
    tagline: 'SilkID fingerprint + face access terminal, TCP/IP + RS485 + Wiegand I/O',
    verified: true,
    datasheetUrl: 'https://www.zkteco.com/en/product_detail/ProBio.html',
    specs: {
      technology: ['mifare', 'em-marin-125khz'],
      protocol: ['wiegand-26', 'osdp'],
      keypad: true,
    },
  },

  {
    id: 'zkteco-proface-x',
    manufacturer: 'ZKTeco',
    model: 'ProFace X',
    category: 'access-reader',
    tagline: 'Outdoor IP68 face terminal, ZKFace 3.6 up to 100,000 templates',
    verified: true,
    datasheetUrl: 'https://www.zkteco.com/en/Hybrid_BiometricHNf/ProFace_X',
    specs: {
      technology: ['mifare', 'em-marin-125khz'],
      protocol: ['wiegand-26', 'osdp'],
      outdoor: true,
      keypad: false,
      ipRating: 'IP68',
    },
  },

  {
    id: 'zkteco-speedface-v5l',
    manufacturer: 'ZKTeco',
    model: 'SpeedFace-V5L',
    category: 'access-reader',
    tagline: 'Masked-face capable hybrid biometric terminal, ZKFace + Z-ID fingerprint',
    verified: true,
    datasheetUrl: 'https://www.zkteco.com/en/HybridBiometric4ZM/SpeedFace-V5L',
    specs: {
      technology: ['mifare', 'em-marin-125khz'],
      keypad: true,
    },
  },

  {
    id: 'zkteco-g4-pro',
    manufacturer: 'ZKTeco',
    model: 'G4 Pro',
    category: 'access-reader',
    tagline: 'Octa-core face/palm/fingerprint terminal, PoE 802.3af/at, −25 to +65 °C',
    verified: true,
    datasheetUrl: 'https://www.zkteco.com/en/Hybrid_Biometric/G4_Pro',
    specs: {
      technology: ['mifare', 'em-marin-125khz'],
      protocol: ['wiegand-26', 'osdp'],
      keypad: true,
    },
  },

  {
    id: 'zkteco-kr500-series',
    manufacturer: 'ZKTeco',
    model: 'KR500 Series Wiegand Reader',
    category: 'access-reader',
    tagline: 'Outdoor IP65 Wiegand card reader, 125 kHz EM or 13.56 MHz IC variants',
    verified: true,
    datasheetUrl: 'https://www.zkteco.com/en/RFIDReader/KR500-Series',
    specs: {
      technology: ['em-marin-125khz', 'mifare'],
      protocol: ['wiegand-26', 'wiegand-34'],
      outdoor: true,
      keypad: false,
      ipRating: 'IP65',
    },
  },

  {
    id: 'zkteco-kr600-series',
    manufacturer: 'ZKTeco',
    model: 'KR600 Series Wiegand Reader',
    category: 'access-reader',
    tagline: 'Outdoor IP65 mullion Wiegand reader, 125 kHz EM or 13.56 MHz IC',
    verified: true,
    datasheetUrl: 'https://www.zkteco.com/en/RFIDReader/KR600-Series',
    specs: {
      technology: ['em-marin-125khz', 'mifare'],
      protocol: ['wiegand-26', 'wiegand-34'],
      outdoor: true,
      keypad: false,
      ipRating: 'IP65',
    },
  },

  {
    id: 'zkteco-cr10e',
    manufacturer: 'ZKTeco',
    model: 'CR10E Enrollment Reader',
    category: 'access-reader',
    tagline: 'USB plug-and-play 125 kHz enrollment reader for ZKBio software (USB protocol, no Wiegand)',
    verified: true,
    datasheetUrl: 'https://www.zkteco.com/en/',
    specs: {
      technology: ['em-marin-125khz'],
      keypad: false,
    },
  },

  {
    id: 'zkteco-fr1200',
    manufacturer: 'ZKTeco',
    model: 'FR1200',
    category: 'access-reader',
    tagline: 'RS485/OSDP fingerprint slave reader for inBio controllers, IP65',
    verified: true,
    datasheetUrl: 'https://www.zkteco.com/en/BiometricReader/FR1200',
    specs: {
      technology: ['em-marin-125khz', 'mifare'],
      protocol: ['osdp'],
      outdoor: true,
      keypad: false,
      ipRating: 'IP65',
    },
  },

  {
    id: 'zkteco-fr1300',
    manufacturer: 'ZKTeco',
    model: 'FR1300',
    category: 'access-reader',
    tagline: 'Indoor RS485 fingerprint slave reader for inBio biometric panels',
    verified: true,
    datasheetUrl: 'https://www.zkteco.com/en/BiometricReader/FR1300',
    specs: {
      technology: ['em-marin-125khz'],
      protocol: ['osdp'],
      keypad: false,
    },
  },

  {
    id: 'zkteco-fr1500s',
    manufacturer: 'ZKTeco',
    model: 'FR1500S',
    category: 'access-reader',
    tagline: 'SUS304 stainless IP65 fingerprint slave reader, RS485/OSDP, AES128',
    verified: true,
    datasheetUrl: 'https://www.zkteco.com/en/BiometricReader/FR1500S',
    specs: {
      technology: ['em-marin-125khz', 'mifare'],
      protocol: ['osdp'],
      outdoor: true,
      keypad: false,
      ipRating: 'IP65',
    },
  },

  // ─────────────────────────────────────────────────────────────
  // ZKTeco — controllers (inBio, C3, Atlas)
  // ─────────────────────────────────────────────────────────────

  {
    id: 'zkteco-inbio260',
    manufacturer: 'ZKTeco',
    model: 'inBio260 Pro Plus',
    category: 'door-controller',
    tagline: '2-door biometric IP controller, 4 RS485/OSDP + 4 Wiegand reader ports',
    verified: true,
    datasheetUrl: 'https://www.zkteco.com/en/Control_Panel/InBio_Pro_Plus_Series',
    specs: {
      doors: 2,
      hostBus: 'tcp-ip',
    },
  },

  {
    id: 'zkteco-inbio460',
    manufacturer: 'ZKTeco',
    model: 'inBio460 Pro Plus',
    category: 'door-controller',
    tagline: '4-door biometric IP controller, 8 RS485/OSDP + 4 Wiegand reader ports',
    verified: true,
    datasheetUrl: 'https://www.zkteco.com/en/Control_Panel/InBio_Pro_Plus_Series',
    specs: {
      doors: 2,
      hostBus: 'tcp-ip',
    },
  },

  {
    id: 'zkteco-c3-100',
    manufacturer: 'ZKTeco',
    model: 'C3-100',
    category: 'door-controller',
    tagline: '1-door IP access panel, 2 Wiegand readers, 30,000 cardholders',
    verified: true,
    datasheetUrl: 'https://www.zkteco.com/en/RFIDControlPanelEnl/C3_Plus_Series',
    specs: {
      doors: 1,
      hostBus: 'tcp-ip',
    },
  },

  {
    id: 'zkteco-c3-200',
    manufacturer: 'ZKTeco',
    model: 'C3-200',
    category: 'door-controller',
    tagline: '2-door IP access panel, 4 Wiegand readers, 30,000 cardholders',
    verified: true,
    datasheetUrl: 'https://www.zkteco.com/en/RFIDControlPanelEnl/C3_Plus_Series',
    specs: {
      doors: 2,
      hostBus: 'tcp-ip',
    },
  },

  {
    id: 'zkteco-c3-400',
    manufacturer: 'ZKTeco',
    model: 'C3-400',
    category: 'door-controller',
    tagline: '4-door IP access panel, 4 Wiegand readers, 30,000 cardholders',
    verified: true,
    datasheetUrl: 'https://www.zkteco.com/en/RFIDControlPanelEnl/C3_Plus_Series',
    specs: {
      doors: 2,
      hostBus: 'tcp-ip',
    },
  },

  {
    id: 'zkteco-atlas-200',
    manufacturer: 'ZKTeco',
    model: 'Atlas-200',
    category: 'door-controller',
    tagline: '2-door PoE web-managed controller, up to 4 Wiegand or OSDP readers',
    verified: true,
    datasheetUrl: 'https://www.zkteco.com/en/Atlas_Series/Atlas-Prox-Series',
    specs: {
      doors: 2,
      hostBus: 'tcp-ip',
    },
  },

  {
    id: 'zkteco-atlas-400',
    manufacturer: 'ZKTeco',
    model: 'Atlas-400',
    category: 'door-controller',
    tagline: '4-door PoE web-managed controller, up to 4 Wiegand or 8 OSDP readers',
    verified: true,
    datasheetUrl: 'https://www.zkteco.com/en/Atlas_Series/Atlas-Prox-Series',
    specs: {
      doors: 2,
      hostBus: 'tcp-ip',
    },
  },

  // ─────────────────────────────────────────────────────────────
  // 2N — Access Unit 2.0 readers
  // ─────────────────────────────────────────────────────────────

  {
    id: '2n-access-unit-2-bluetooth-rfid',
    manufacturer: '2N',
    model: 'Access Unit 2.0 Bluetooth & RFID',
    category: 'access-reader',
    tagline: 'IP54/IK08 networked reader, dual-freq RFID plus Bluetooth 5.0 mobile credential',
    verified: true,
    datasheetUrl: 'https://www.2n.com/en-US/products/2n-access-unit-2-0-bluetooth-rfid',
    specs: {
      technology: ['em-marin-125khz', 'mifare'],
      outdoor: true,
      keypad: false,
      ipRating: 'IP54',
    },
  },

  {
    id: '2n-access-unit-2-touch-keypad-bluetooth-rfid',
    manufacturer: '2N',
    model: 'Access Unit 2.0 Touch Keypad, Bluetooth & RFID',
    category: 'access-reader',
    tagline: 'Three-in-one reader: dual-freq RFID, Bluetooth mobile key, capacitive PIN keypad',
    verified: true,
    datasheetUrl: 'https://www.2n.com/en-US/products/2n-access-unit-2-touch-keypad-bluetooth-rfid',
    specs: {
      technology: ['em-marin-125khz', 'mifare'],
      outdoor: true,
      keypad: true,
      ipRating: 'IP54',
    },
  },

  {
    id: '2n-access-unit-2-touch-keypad-rfid',
    manufacturer: '2N',
    model: 'Access Unit 2.0 Touch Keypad & RFID',
    category: 'access-reader',
    tagline: 'IP54/IK08 reader combining 125 kHz + 13.56 MHz RFID with capacitive PIN keypad',
    verified: true,
    datasheetUrl: 'https://www.2n.com/en-US/products/2n-access-unit-2-0-touch-keypad-rfid',
    specs: {
      technology: ['em-marin-125khz', 'mifare'],
      outdoor: true,
      keypad: true,
      ipRating: 'IP54',
    },
  },

  {
    id: '2n-access-unit-m-touch-keypad-rfid',
    manufacturer: '2N',
    model: 'Access Unit M Touch Keypad & RFID',
    category: 'access-reader',
    tagline: 'Compact M-series IP reader with capacitive PIN keypad and dual-freq RFID',
    verified: true,
    datasheetUrl: 'https://www.2n.com/en-US/products/2n-access-unit-m-touch-keypad-rfid',
    specs: {
      technology: ['em-marin-125khz', 'mifare'],
      keypad: true,
    },
  },
];
